package com.essntl.features.message.domain.model

import core.dateformat.utils.formatTimestampToHHMM
import core.dateformat.utils.formatTimestampToHHMMPeriod
import core.dateformat.utils.formatToFullString
import com.essntl.core.utils.utils.capitalizeFirstLetter
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.service.domain.model.service.ServiceModel
import kotlinx.datetime.LocalDate

data class MessageModel(
    val id: String,
    val createdAt: String,
    val userText: String,
    val serviceList: List<ServiceModel>,
    val categoryList: List<CategoryModel>,
    val itineraryServiceList: List<ItineraryServiceModel>,
    val client: ClientModel,
    val sender: ProfileModel,
    val type: Type,
    val platformId: String,
    val itineraryService: ItineraryServiceModel?,
) {
    sealed interface Type {
        data class Email(
            val email: String,
            val cc: String? = null,
            val from: String? = null,
            val bcc: String? = null,
            val replyTo: String? = null,
            val multiTemplateId: String? = null,
            val simpleTemplateId: String? = null,
        ) : Type

        data class Sms(val phone: String) : Type

        data class Whatsapp(val phone: String) : Type
    }

    fun getClientMessageString(
        service: ServiceModel,
        itineraryService: ItineraryServiceModel,
    ): String =
        messageStringBuilder(
            service = service,
            itineraryService = itineraryService,
            client = client,
            forClient = true,
            forSupplierTravelAgent = false,
        )

    companion object {
        val empty =
            MessageModel(
                id = "",
                createdAt = "",
                userText = "",
                serviceList = emptyList(),
                categoryList = emptyList(),
                itineraryServiceList = emptyList(),
                client = ClientModel.empty,
                sender = ProfileModel.empty,
                type = Type.Email(""),
                platformId = "",
                itineraryService = null,
            )

        fun getClientTomorrowMessageString(
            client: ClientModel?,
            service: ServiceModel,
            category: CategoryModel,
            itineraryService: ItineraryServiceModel,
            sender: ProfileModel? = null,
        ): String {
            val stringBuilder = StringBuilder()

            val startDate =
                try {
                    LocalDate.parse(itineraryService.startDate)
                        .formatToFullString()
                } catch (e: Exception) {
                    ""
                }

            val clientSalutation = client?.salutation ?: client?.firstName?.capitalizeFirstLetter()
            stringBuilder.appendLine("Dear $clientSalutation,")
            stringBuilder.appendLine("")
            stringBuilder.appendLine("Tomorrow, $startDate,")
            stringBuilder.appendLine("")

            val tourConfirmed =
                "your ${category.name.capitalizeFirstLetter()}" +
                    " for ${itineraryService.guestCount} is confirmed:"

            if (itineraryService.startTime != null) {
                stringBuilder.appendLine(
                    "At ${formatTimestampToHHMMPeriod(itineraryService.startTime!!)}, $tourConfirmed",
                )
            } else {
                stringBuilder.appendLine(tourConfirmed.capitalizeFirstLetter())
            }

            stringBuilder.appendLine(
                "${category.name.capitalizeFirstLetter()}: " +
                    service.title.capitalizeFirstLetter(),
            )

            itineraryService.supplierContact?.let {
                if (!it.firstName.isNullOrEmpty() ||
                    !it.lastName.isNullOrEmpty()
                )
                    stringBuilder.appendLine("🙋 Contact: ${it.prettyName}")
            }

            itineraryService.supplierContact?.phoneNumber?.let {
                stringBuilder.appendLine("📱 Cell: +$it")
            }

            if (itineraryService.address != null) {
                stringBuilder.appendLine("📍 Address: ${itineraryService.address?.description}")
            }
            if (itineraryService.meetingPoint != null) stringBuilder.appendLine("📍 Meeting point: ${itineraryService.meetingPoint}")

            if (itineraryService.dynamicFieldsString.isNotEmpty()) {
                stringBuilder.appendLine(itineraryService.dynamicFieldsString)
            }

            itineraryService.supplierContact?.let {
                if (!it.firstName.isNullOrEmpty() ||
                    !it.lastName.isNullOrEmpty()
                ) {
                    stringBuilder.appendLine("")
                    stringBuilder.appendLine("Feel free to contact your driver/guide directly if you have trouble finding one another.")
                }
            }

            stringBuilder.appendLine("")
            stringBuilder.appendLine("We remain at your disposal should you require any further assistance.")

            var senderString = "Be Essential Club ✨"
            sender?.firstName?.let {
                senderString = "${it.capitalizeFirstLetter()} @ $senderString"
            }
            stringBuilder.appendLine(senderString)

            return stringBuilder.toString().trimIndent()
        }

        fun messageStringBuilder(
            itineraryServiceList: List<ItineraryServiceModel>,
            client: ClientModel?,
            forClient: Boolean,
            sender: ProfileModel? = null,
        ): String {
            val stringBuilder = StringBuilder()

            if (forClient) {
                val clientSalutation = client?.salutation ?: client?.firstName?.capitalizeFirstLetter()
                stringBuilder.appendLine("Dear $clientSalutation,")
                stringBuilder.appendLine("You have the following services confirmed:")
            }

            itineraryServiceList.forEach { itineraryService ->
                val messageBody =
                    messageBodyStringBuilder(
                        service = itineraryService.service,
                        itineraryService = itineraryService,
                        client = client,
                        forClient = forClient,
                    )
                stringBuilder.appendLine()
                stringBuilder.appendLine(messageBody)
            }

            stringBuilder.appendLine("")
            stringBuilder.appendLine("We remain at your disposal should you require any further assistance.")

            var senderString = "Be Essential Club ✨"
            sender?.firstName?.let {
                senderString = "${it.capitalizeFirstLetter()} @ $senderString"
            }
            stringBuilder.appendLine(senderString)

            return stringBuilder.toString().trimIndent()
        }

        fun messageStringBuilder(
            service: ServiceModel,
            itineraryService: ItineraryServiceModel,
            client: ClientModel?,
            forClient: Boolean,
            forSupplierTravelAgent: Boolean,
        ): String {
            val stringBuilder = StringBuilder()

            if (forClient) {
                val clientSalutation = client?.salutation ?: client?.firstName?.capitalizeFirstLetter()
                stringBuilder.appendLine("Dear $clientSalutation,")
                stringBuilder.appendLine("You have a confirmed service with the following details:")
            }

            if (forSupplierTravelAgent) {
                stringBuilder.appendLine("Service details for client: ${client?.prettyName}")
                stringBuilder.appendLine("")
            }

            val messageBody =
                messageBodyStringBuilder(
                    service = service,
                    itineraryService = itineraryService,
                    client = client,
                    forClient = forClient,
                )

            stringBuilder.appendLine(messageBody)

            return stringBuilder.toString().trimIndent()
        }

        private fun messageBodyStringBuilder(
            service: ServiceModel,
            itineraryService: ItineraryServiceModel,
            client: ClientModel?,
            forClient: Boolean,
        ): String {
            val stringBuilder = StringBuilder()
            val serviceTitle = service.title.trimIndent().capitalizeFirstLetter()
            val supplier = itineraryService.supplier?.prettyName.orEmpty().trimIndent().capitalizeFirstLetter()

            if (forClient) {
                stringBuilder.appendLine(serviceTitle)
            } else {
                stringBuilder.appendLine("$serviceTitle     -      $supplier")
            }

            val startDate =
                try {
                    LocalDate.parse(itineraryService.startDate)
                        .formatToFullString()
                } catch (e: Exception) {
                    ""
                }

            stringBuilder.appendLine("🗓️ Date: $startDate")

            // Start Time & End Time
            if (itineraryService.startTime != null && itineraryService.endTime != null) {
                stringBuilder.appendLine(
                    "🕐 Start: ${formatTimestampToHHMM(itineraryService.startTime!!)}  -" +
                        "   ⏳ End: ${formatTimestampToHHMM(itineraryService.endTime!!)}",
                )
            } else if (itineraryService.startTime != null) {
                stringBuilder.appendLine("🕐 Start: ${formatTimestampToHHMM(itineraryService.startTime!!)}")
            }

            val guestCount = "👥 ${itineraryService.guestCount} people"

            // Client info for PO
            if (!forClient && client != null) {
                val clientName =
                    "👤 ${client.firstName?.capitalizeFirstLetter() ?: ""} " +
                        (client.lastName.capitalizeFirstLetter()) +
                        "   -   $guestCount"
                stringBuilder.appendLine(clientName)
            } else if (forClient) {
                stringBuilder.appendLine(guestCount)
            }

            // Address & meeting point
            if (itineraryService.address != null) stringBuilder.appendLine("📍 Address: ${itineraryService.address?.description}")
            if (itineraryService.meetingPoint != null) stringBuilder.appendLine("📍 Meeting point: ${itineraryService.meetingPoint}")

            if (forClient) {
                itineraryService.supplierContact?.let {
                    if (!it.firstName.isNullOrEmpty() ||
                        !it.lastName.isNullOrEmpty()
                    )
                        stringBuilder.appendLine("🙋 Contact: ${it.prettyName}")
                }

                itineraryService.supplierContact?.phoneNumber?.let {
                    stringBuilder.appendLine("📱 Cell: +$it")
                }
            }

            // PO Special notes and extra fields
            if (!forClient) {
                itineraryService.specialNotes?.let {
                    stringBuilder.appendLine("📝 $it")
                }

                // dynamic fields
                if (itineraryService.dynamicFieldsString.isNotEmpty()) {
                    stringBuilder.appendLine(itineraryService.dynamicFieldsString)
                }
            }

            return stringBuilder.toString().trimIndent()
        }
    }
}
